// src/scenes/BaseGameScene.js
// v11 - Increased trail duration, increased scores x10.

import Phaser from 'phaser';
import { auth, db } from '../firebaseConfig.js';
import CanvasFixer from '../canvasFixer.js';

const GAME_STATES = {
  NORMAL: 'normal',
  HYPERSPACE: 'hyperspace',
  PAUSED: 'paused',
  GAME_OVER: 'gameOver'
};

const DEPTHS = {
    BACKGROUND: -10, GHOST_SHIPS: 5, GAME_OBJECTS: 10, EFFECTS: 60,
    TRAILS: 45, PLAYER: 50, BASE_UI: 950, DASHBOARD: 900,
    FLOATING_NOTIFICATIONS: 1000, OVERLAYS: 5000,
};

const HYPERSPACE_DURATION = 15000;
const HYPERSPACE_TRAIL_MULTIPLIER = 15; // Keep high multiplier for hyperspace trail hits
const HYPERSPACE_GREEN_CHANCE = 0.25;
const HYPERSPACE_ASTEROID_INTERVAL = 700;

// --- Score Constants ---
const SCORE_FACTOR = 10; // Multiply all base scores by this
const SCORE_ASTEROID_TRAIL = 10 * SCORE_FACTOR;
const SCORE_GREEN_ASTEROID_TRAIL = 50 * SCORE_FACTOR;
const SCORE_SPECIAL_ASTEROID_TRAIL = 100 * SCORE_FACTOR; // Triggers hyperspace
const SCORE_HYPERSPACE_ASTEROID_TRAIL = 20 * SCORE_FACTOR; // Base for hyperspace trail hits
const SCORE_HYPERSPACE_GREEN_TRAIL = 30 * SCORE_FACTOR; // Base for green in hyperspace trail hits
const SCORE_HYPERSPACE_BOX_HIT = 50 * SCORE_FACTOR; // Hitting cyan asteroid in target box
const SCORE_BRICK_BREAK = 10 * SCORE_FACTOR;
// --- END Score Constants ---

// --- NEW: Trail Duration ---
const TRAIL_FADE_DURATION = 3000; // Increased duration in ms (was 800)
const TRAIL_FADE_DELAY = 250;    // Delay before fade starts (was 100)
const HYPERSPACE_TRAIL_FADE_DURATION = 800; // Keep hyperspace trails shorter (was 600)
const HYPERSPACE_TRAIL_FADE_DELAY = 50;     // Keep hyperspace trails shorter (was 50)
// --- END NEW ---

// --- Hyperspace Asteroid Speed (New Constants) ---
const HYPERSPACE_ASTEROID_MIN_SPEED = -230; // Approx -350 * (1 - 0.35)
const HYPERSPACE_ASTEROID_MAX_SPEED = -160; // Approx -250 * (1 - 0.35)
// --- End Hyperspace Asteroid Speed ---


export default class BaseGameScene extends Phaser.Scene {

  static get GAME_STATES() { return GAME_STATES; }

  constructor(config) {
    super(config);
    // Initialize properties
    this.gameState = BaseGameScene.GAME_STATES.NORMAL;
    this.gameOver = false; this.gameOverProcessed = false; this.isAlive = true;
    this.lives = 3; this.score = 0; this.player = null; this.blueBoxes = [];
    this.trails = null; this.asteroids = null; this.bricks = null;
    this.greenAsteroidTimer = null; this.specialAsteroidTimer = null; this.hyperspaceAsteroidTimer = null;
    this.pausedAsteroids = []; this.hyperspaceMode = false; this.hyperspaceDuration = HYPERSPACE_DURATION;
    this.hyperspaceTriggeredBy = null; this.playerInvincible = false; this.hyperspaceBoxes = [];
    this.hyperspaceColliders = []; this.hyperspaceBackground = null; this.starParticles = [];
    this.starTimers = []; this.hyperspaceTimer = null; this.exitingHyperspace = false;
    this.originalPlayerPos = { x: 0, y: 0 }; this.playerShield = null; this.tempPlayerShield = null;
    this.invincibilityTimer = null; this.shortcutSequence = []; this.lastKeyTime = 0;
    this.sequenceTimeout = 3000; this.lastFpsUpdateTime = 0; this.fpsUpdateInterval = 500;
    this.frameCounter = 0; this.currentFps = 60; this.performanceMode = false;
    this.lowFpsThreshold = 40; this.lowFpsCounter = 0; this.updateFrequencyMultiplier = 1;
    this.updateFrameSkip = 0; this.lastFrozenAsteroidCheck = 0; this.uiPanel = null;
    this.statsTitle = null; this.livesText = null; this.shortcutText = null; this.scoreText = null;
    this.alertsPanel = null; this.alertsText = null; this.gameOverText = null;
    this.hyperspaceModeText = null; this.assetsLoaded = new Set(); this.visibilityChangeListener = null;
    this.padding = 20; this.dashboard = null; this.activeNotification = null; this.shownNotifications = new Set();
    
    // Initialize UI readiness flag for SceneManager transition timing
    this.isUIReady = false;
  }

  // --- Scene Lifecycle Methods ---

  init() {
    console.debug(`(${this.scene.key}) Initializing...`);
    this.lives = 3; this.score = 0; this.gameOver = false; this.gameOverProcessed = false;
    this.isAlive = true; this.gameState = BaseGameScene.GAME_STATES.NORMAL;
    this.hyperspaceMode = false; this.exitingHyperspace = false; this.playerInvincible = false;
    this.shortcutSequence = []; this.lastKeyTime = 0; this.frameCounter = 0;
    this.lastFpsUpdateTime = 0; this.lowFpsCounter = 0; this.updateFrameSkip = 0;
    this.lastFrozenAsteroidCheck = 0; this.pausedAsteroids = []; this.shownNotifications.clear();
    const isMobile = /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
    const isLowEndHardware = window.navigator.hardwareConcurrency <= 4;
    this.performanceMode = isMobile || isLowEndHardware;
    this.updateFrequencyMultiplier = this.performanceMode ? 2 : 1;
    console.debug(`(${this.scene.key}) Performance Mode: ${this.performanceMode}`);
  }

  preload() {
    console.debug(`(${this.scene.key}) Preloading...`);
    this.load.on('loaderror', (fileObj) => console.error('Asset load error:', fileObj.key, fileObj.url));
    const assetsToLoad = [ { key: "player", path: "assets/player.png" }, { key: "asteroid", path: "assets/asteroid.png" }, { key: "brick", path: "assets/brick.png" }, { key: "greenAsteroid", path: "assets/greenAsteroid.png" } ];
    assetsToLoad.forEach(asset => { if (!this.textures.exists(asset.key) && !this.assetsLoaded.has(asset.key)) { this.load.image(asset.key, asset.path); this.assetsLoaded.add(asset.key); } });
  }

  create() {
    console.debug(`(${this.scene.key}) Creating scene objects...`);
    if(this.game.canvas) { this.game.canvas.setAttribute("tabindex", "0"); this.game.canvas.focus(); }
    const ribbon = document.getElementById("top-ribbon"); if (ribbon) ribbon.style.display = "flex";
    if (!this.cameras?.main) { console.error("Camera not ready in create!"); return; }
    this.cameras.main.setBackgroundColor("#050010");
    if(!this.physics.world) { console.error("Physics world not ready in create!"); return; }
    const width = this.cameras.main.width; const height = this.cameras.main.height;
    this.physics.world.setBounds(0, 0, width, height);
    this.optimizeForMobile();
    this.trails = this.physics.add.staticGroup();
    this.asteroids = this.physics.add.group({ runChildUpdate: true });
    this.bricks = this.physics.add.staticGroup({ defaultKey: 'brick' });
    this.player = this.physics.add.sprite(width / 2, height * 0.85, "player").setImmovable(true).setCollideWorldBounds(true).setDepth(DEPTHS.PLAYER);
    this.blueBoxes = []; const boxColor = 0x0033FF; const boxAlpha = 0.3; const boxSize = 60;
    for (let i = 0; i < 4; i++) { const box = this.add.rectangle(0, 0, boxSize, boxSize, boxColor, boxAlpha).setStrokeStyle(2, 0x0088FF, 0.6).setDepth(DEPTHS.GAME_OBJECTS - 1); this.blueBoxes.push(box); }
    this.updateBlueBoxPositions();
    this.createBrickWall(); this.spawnAsteroid(); this.createUI();
    const greenDelay = Phaser.Math.Between(8000, this.performanceMode ? 15000 : 12000);
    this.greenAsteroidTimer = this.time.addEvent({ delay: greenDelay, callback: () => this.spawnGreenAsteroid(false), callbackScope: this, loop: true });
    const specialDelay = Phaser.Math.Between(10000, this.performanceMode ? 20000 : 15000);
    this.specialAsteroidTimer = this.time.addEvent({ delay: specialDelay, callback: this.spawnSpecialAsteroid, callbackScope: this, loop: true });
    this.setupColliders();
    this.input.keyboard.on("keydown", this.handleKeyDown, this);
    this.scale.on('resize', this.handleResize, this);
    this.visibilityChangeListener = this.handleTabVisibilityChange.bind(this);
    document.addEventListener('visibilitychange', this.visibilityChangeListener);
    this.activateTemporaryShield(3000); this.movePlayerToStart(); this.handleResize();
    
    // Signal that game scene setup is complete for SceneManager fade-in
    this.isUIReady = true;
    console.debug(`[${this.scene.key}] BaseGameScene create finished, ready for fade-in`);
  }

  update(time, delta) {
    if (this.gameOver || this.gameOverProcessed || this.gameState === BaseGameScene.GAME_STATES.PAUSED) return;
    this.monitorPerformance(time); this.checkGameOverConditions(); if (this.gameOver) return;
    this.updateFrameSkip++; if (this.performanceMode && this.updateFrameSkip % this.updateFrequencyMultiplier !== 0) return; if (this.updateFrameSkip > 1000) this.updateFrameSkip = 0;
    if (time - this.lastFrozenAsteroidCheck > 5000) { this.checkForFrozenAsteroids(); this.lastFrozenAsteroidCheck = time; }
    if (this.hyperspaceMode && this.playerShield?.active) this.playerShield.setPosition(this.player.x, this.player.y);
    if (this.tempPlayerShield?.active) this.tempPlayerShield.setPosition(this.player.x, this.player.y);
    if (this.hyperspaceMode) this.updateHyperspaceTimerUI(); else if (this.hyperspaceModeText) { this.hyperspaceModeText.destroy(); this.hyperspaceModeText = null; }
    this.customUpdate(time, delta);
  }

  shutdown() {
    const sceneKey = this.scene?.key || 'BaseGameScene'; console.debug(`(${sceneKey}) Shutting down...`);
    console.debug(`(${sceneKey}) Removing timers...`); const removeTimer = (timer) => { if (timer) try { timer.remove(); } catch(e){} return null; }
    this.greenAsteroidTimer = removeTimer(this.greenAsteroidTimer); this.specialAsteroidTimer = removeTimer(this.specialAsteroidTimer); this.hyperspaceTimer = removeTimer(this.hyperspaceTimer); this.hyperspaceAsteroidTimer = removeTimer(this.hyperspaceAsteroidTimer); this.invincibilityTimer = removeTimer(this.invincibilityTimer); if (this.starTimers) { this.starTimers.forEach(t => removeTimer(t)); this.starTimers = []; } try { this.time?.removeAllEvents(); } catch(e){}
    console.debug(`(${sceneKey}) Killing tweens...`); try { this.tweens?.killAll(); } catch(e){}
    console.debug(`(${sceneKey}) Removing listeners...`); try { this.input?.keyboard?.off("keydown", this.handleKeyDown, this); } catch(e){} try { this.scale?.off('resize', this.handleResize, this); } catch(e){} if (this.visibilityChangeListener) { try { document.removeEventListener('visibilitychange', this.visibilityChangeListener); } catch(e){} this.visibilityChangeListener = null; }
    console.debug(`(${sceneKey}) Destroying physics objects...`); try { this.physics?.world?.colliders?.destroy(); } catch(e){}
    const destroyGroup = (group) => { if (group) try { group.destroy(true); } catch(e){ console.warn(`(${sceneKey}) Error destroying group:`, e); } return null; }; this.trails = destroyGroup(this.trails); this.asteroids = destroyGroup(this.asteroids); this.bricks = destroyGroup(this.bricks);
    console.debug(`(${sceneKey}) Destroying individual game objects...`); const safeDestroy = (obj) => { if (obj) try { obj.destroy(); } catch(e){} return null; }
    this.player = safeDestroy(this.player); this.blueBoxes?.forEach(b => safeDestroy(b)); this.blueBoxes = []; this.hyperspaceBackground = safeDestroy(this.hyperspaceBackground); this.starParticles?.forEach(p => safeDestroy(p)); this.starParticles = []; this.playerShield = safeDestroy(this.playerShield); this.tempPlayerShield = safeDestroy(this.tempPlayerShield); this.hyperspaceBoxes?.forEach(b => safeDestroy(b)); this.hyperspaceBoxes = [];
    console.debug(`(${sceneKey}) Destroying UI elements...`); this.uiPanel = safeDestroy(this.uiPanel); this.statsTitle = safeDestroy(this.statsTitle); this.livesText = safeDestroy(this.livesText); this.shortcutText = safeDestroy(this.shortcutText); this.scoreText = safeDestroy(this.scoreText); this.alertsPanel = safeDestroy(this.alertsPanel); this.alertsText = safeDestroy(this.alertsText); this.gameOverText = safeDestroy(this.gameOverText); this.hyperspaceModeText = safeDestroy(this.hyperspaceModeText); this.activeNotification?.forEach(el => safeDestroy(el)); this.activeNotification = null; this.dashboard?.container?.destroy(true); this.dashboard = null;
    console.debug(`(${sceneKey}) Resetting state variables...`); this.gameOver = false; this.gameOverProcessed = false; this.isAlive = true; this.gameState = GAME_STATES.NORMAL; this.hyperspaceMode = false; this.playerInvincible = false; this.assetsLoaded.clear();
    console.debug(`(${sceneKey}) Shutdown complete.`);
  }

  handleResize(gameSize) {
    if (!this.cameras?.main) return;
    const width = this.cameras.main.width; const height = this.cameras.main.height;
    const padding = this.padding || 20;
    const panelX = width - 150; const panelY = 80; const panelWidth = 280; const panelHeight = 120;
    const textStartX = panelX - (panelWidth / 2) + 15; const textStartY = panelY - (panelHeight / 2) + 50;
    if (this.uiPanel) this.uiPanel.setPosition(panelX, panelY).setSize(panelWidth, panelHeight);
    if (this.statsTitle) this.statsTitle.setPosition(panelX, panelY - (panelHeight / 2) + 20);
    if (this.livesText) this.livesText.setPosition(textStartX, textStartY);
    if (this.scoreText) this.scoreText.setPosition(textStartX, textStartY + 25);
    if (this.shortcutText) this.shortcutText.setPosition(textStartX, textStartY + 50);
    if (this.dashboard?.container) this.dashboard.container.setPosition(170, 80);
    this.updateBlueBoxPositions();
  }

  checkGameOverConditions() {
    if (this.gameOver || this.gameOverProcessed) return;
    if (this.lives <= 0) { console.log(`(${this.scene.key}) Lives depleted. Triggering game over.`); this.triggerGameOver("lives"); return; }
    try {
        if (!this.asteroids?.getChildren || !this.cameras?.main?.height) return;
        const height = this.cameras.main.height; const brickWallY = height - 20; const boundaryBuffer = 10;
        const activeAsteroids = this.asteroids.getChildren();
        for (let i = 0; i < activeAsteroids.length; i++) {
            const asteroid = activeAsteroids[i];
            if (asteroid?.active && asteroid.body) {
                let skipAsteroid = false; try { if (asteroid.getData) skipAsteroid = asteroid.getData('isGreen') || asteroid.getData('isSpecial') || asteroid.getData('isHyperspace'); } catch (e) {}
                if (!skipAsteroid && asteroid.y > brickWallY + boundaryBuffer) { console.log(`(${this.scene.key}) Asteroid passed boundary. Triggering game over.`); this.triggerGameOver("boundary"); return; }
            }
        }
    } catch (error) { console.warn(`(${this.scene.key}) Error during boundary check:`, error); }
  }

  // --- Game State & UI ---

  setGameState(newState) {
    try {
        const oldState = this.gameState; if (oldState === newState) return;
        console.log(`(${this.scene.key}) State: ${oldState} -> ${newState}`);
        if (oldState === BaseGameScene.GAME_STATES.HYPERSPACE && newState !== BaseGameScene.GAME_STATES.HYPERSPACE) {
            this.hyperspaceMode = false; this.exitingHyperspace = false; this.hyperspaceColliders?.forEach(c => c?.destroy()); this.hyperspaceColliders = []; this.hyperspaceAsteroidTimer?.remove(); this.hyperspaceAsteroidTimer = null;
            this.asteroids?.getChildren().forEach(a => { if (a?.getData("isHyperspaceSpecial")) a.destroy(); else if (a) { a.setTexture("asteroid").clearTint().setData("isHyperspaceSpecial", false); } });
        }
        if (newState === BaseGameScene.GAME_STATES.PAUSED) this.physics?.pause();
        else if (newState === BaseGameScene.GAME_STATES.NORMAL && oldState === BaseGameScene.GAME_STATES.PAUSED) this.physics?.resume();
        else if (newState === BaseGameScene.GAME_STATES.GAME_OVER) { this.gameOver = true; this.isAlive = false; }
        this.gameState = newState; if (typeof this.onGameStateChanged === 'function') this.onGameStateChanged(oldState, newState);
    } catch (e) { console.warn("Error in setGameState:", e); this.gameState = newState; if (newState === BaseGameScene.GAME_STATES.GAME_OVER) { this.gameOver = true; this.isAlive = false; } }
  }

  onGameStateChanged(oldState, newState) { /* For subclasses */ }

  createUI() {
    const w = this.cameras.main.width; const h = this.cameras.main.height; const padding = this.padding || 20;
    const panelX = w - 150; const panelY = 80; const panelWidth = 280; const panelHeight = 120;
    this.uiPanel = this.add.rectangle(panelX, panelY, panelWidth, panelHeight, 0x000033, 0.7).setOrigin(0.5).setStrokeStyle(2, 0x3366FF).setDepth(DEPTHS.BASE_UI).setScrollFactor(0);
    this.statsTitle = this.add.text(panelX, panelY - (panelHeight / 2) + 20, "GAME STATS", { fontFamily: "Arial", fontSize: "18px", fontWeight: "bold", color: "#FFFFFF", stroke: "#000066", strokeThickness: 2 }).setOrigin(0.5).setDepth(DEPTHS.BASE_UI + 1).setScrollFactor(0);
    const textStartX = panelX - (panelWidth / 2) + 15; const textStartY = panelY - (panelHeight / 2) + 50;
    this.livesText = this.add.text(textStartX, textStartY, `Lives: ${this.lives}`, { fontSize: "16px", fill: "#fff", fontFamily: 'monospace' }).setOrigin(0, 0.5).setDepth(DEPTHS.BASE_UI + 1).setScrollFactor(0);
    this.scoreText = this.add.text(textStartX, textStartY + 25, `Score: ${this.score}`, { fontSize: "16px", fill: "#fff", fontFamily: 'monospace' }).setOrigin(0, 0.5).setDepth(DEPTHS.BASE_UI + 1).setScrollFactor(0);
    this.shortcutText = this.add.text(textStartX, textStartY + 50, "Move: Alt+H+G+A+(L/R/T/B)", { fontSize: "12px", fill: "#ccc", fontFamily: 'monospace' }).setOrigin(0, 0.5).setDepth(DEPTHS.BASE_UI + 1).setScrollFactor(0);
    this.alertsText = this.add.text(0, 0, "", { fontSize: "1px" }).setVisible(false).setAlpha(0);
    this.updateBlueBoxPositions();
  }

  updateBlueBoxPositions() {
    if (!this.blueBoxes || this.blueBoxes.length < 4 || !this.cameras?.main) return;
    const w = this.cameras.main.width; const h = this.cameras.main.height;
    const marginX = w * 0.15; const marginY = h * 0.20;
    const topY = marginY; const bottomY = h - marginY; const leftX = marginX; const rightX = w - marginX;
    const positions = [ { x: leftX, y: topY }, { x: rightX, y: topY }, { x: leftX, y: bottomY }, { x: rightX, y: bottomY } ];
    positions.forEach((pos, i) => { if (this.blueBoxes[i]?.active) this.blueBoxes[i].setPosition(pos.x, pos.y); else if (this.blueBoxes[i]) this.blueBoxes[i].setVisible(false); });
  }

  updateHyperspaceTimerUI() {
    let timeLeftText = "HYPERSPACE ACTIVE";
    if (this.hyperspaceTimer?.getElapsed) { const remainingMs = this.hyperspaceDuration - this.hyperspaceTimer.getElapsed(); if (remainingMs > 0) timeLeftText = `HYPERSPACE: ${Math.ceil(remainingMs / 1000)}s`; }
    if (this.hyperspaceModeText) this.hyperspaceModeText.setText(timeLeftText);
    else this.hyperspaceModeText = this.add.text(10, 100, timeLeftText, { fontSize: "16px", fill: "#00FFFF", stroke: "#000", strokeThickness: 3, fontFamily: 'monospace' }).setDepth(DEPTHS.FLOATING_NOTIFICATIONS + 1).setScrollFactor(0);
  }

  initializeDashboard() { /* ... Basic implementation from previous response ... */
    this.dashboard = { container: this.add.container(170, 80).setDepth(DEPTHS.DASHBOARD).setScrollFactor(0), eventMessages: [], addEvent: (message, color = "#FFFFFF") => { this.dashboard.eventMessages.unshift({ text: message, color: color, time: Date.now() }); if (this.dashboard.eventMessages.length > 3) this.dashboard.eventMessages.pop(); this.updateDashboardEvents(); }, updatePosition: () => { this.dashboard.container?.setPosition(170, 80); } }; const dashboardBg = this.add.rectangle(0, 0, 320, 120, 0x000033, 0.7).setOrigin(0.5).setStrokeStyle(2, 0x3366FF); const title = this.add.text(0, -45, "GAME EVENTS", { fontFamily: "Arial", fontSize: "18px", fontWeight: "bold", color: "#FFFFFF", stroke: "#000066", strokeThickness: 2 }).setOrigin(0.5); this.dashboard.eventsContainer = this.add.container(0, 0); this.dashboard.container.add([dashboardBg, title, this.dashboard.eventsContainer]); this.updateDashboardEvents();
  }
  updateDashboardEvents() { /* ... Basic implementation from previous response ... */
    if (!this.dashboard?.eventsContainer) return; this.dashboard.eventsContainer.removeAll(true); let yPos = -25; this.dashboard.eventMessages.forEach((event, index) => { const bgAlpha = index % 2 === 0 ? 0.5 : 0.3; const eventBg = this.add.rectangle(0, yPos, 300, 24, 0x000022, bgAlpha).setOrigin(0.5); const eventText = this.add.text(-140, yPos, event.text, { fontFamily: "Arial", fontSize: "14px", color: event.color, wordWrap: { width: 280 } }).setOrigin(0, 0.5); this.dashboard.eventsContainer.add([eventBg, eventText]); yPos += 25; });
  }
  showNotification(message, color = "#FFFFFF", duration = 2000) { /* ... Implementation from previous response ... */
    if (!this.scene?.systems?.scenePlugin?.isActive(this.scene.key) || !this.add || !this.tweens) return; const notificationKey = `${message}_${Math.floor(Date.now() / 5000)}`; if (this.shownNotifications?.has(notificationKey)) return; if (!this.shownNotifications) this.shownNotifications = new Set(); this.shownNotifications.add(notificationKey); this.time?.delayedCall(5000, () => this.shownNotifications?.delete(notificationKey)); if (this.activeNotification) { this.tweens?.killTweensOf(this.activeNotification); this.activeNotification.forEach(el => el?.destroy()); this.activeNotification = null; } const width = this.cameras.main.width; const height = this.cameras.main.height; const notificationY = height * 0.15; const container = this.add.container(width / 2, notificationY - 30).setDepth(DEPTHS.FLOATING_NOTIFICATIONS).setAlpha(0); const textStyle = { fontFamily: 'monospace', fontSize: '18px', fontWeight: 'bold', color: color, stroke: '#000000', strokeThickness: 4, backgroundColor: '#00000099', padding: { x: 15, y: 8 }, align: 'center' }; const msgText = this.add.text(0, 0, message, textStyle).setOrigin(0.5); container.add(msgText); this.activeNotification = [container, msgText]; this.tweens.add({ targets: container, y: notificationY, alpha: 1, scale: { from: 0.8, to: 1 }, duration: 300, ease: 'Back.easeOut', onComplete: () => { if (!container.active) return; this.time.delayedCall(duration, () => { if (!container.active) return; this.tweens.add({ targets: container, y: notificationY - 30, alpha: 0, scale: 0.8, duration: 300, ease: 'Cubic.easeIn', onComplete: () => { if (container.active) container.destroy(); if (this.activeNotification && this.activeNotification[0] === container) this.activeNotification = null; } }); }); } }); return container;
  }

  triggerGameOver(reason = "unknown") {
    if (this.gameOver || this.gameOverProcessed) return;
    console.log(`(${this.scene.key}) Triggering Game Over. Reason: ${reason}`);
    this.setGameState(BaseGameScene.GAME_STATES.GAME_OVER); // Sets gameOver = true

    if (this.physics?.world && !this.physics.world.isPaused) this.physics.pause();

    if (typeof this.onGameOver === 'function') this.onGameOver(reason);
    else { console.warn(`(${this.scene.key}) onGameOver method not found! Using fallback.`); this.showFallbackGameOver(); }
  }

  onGameOver(reason = "unknown") {
    console.log(`(${this.scene.key}) Base onGameOver called. Reason: ${reason}. Final Score: ${this.score}`);
    this.gameOverProcessed = true;
    if (!this.hasCustomGameOver()) this.showFallbackGameOver();
  }

  showFallbackGameOver() { /* ... Implementation from previous response ... */
    try { const overlayId = 'fallback-gameover-overlay'; let overlay = document.getElementById(overlayId); if (!overlay) { overlay = document.createElement('div'); overlay.id = overlayId; Object.assign(overlay.style, { position: 'fixed', top: '0', left: '0', width: '100%', height: '100%', backgroundColor: 'rgba(0, 0, 20, 0.85)', zIndex: '20000', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', fontFamily: 'monospace', color: 'white', textAlign: 'center' }); overlay.innerHTML = `<h1 style="color: #FF4444; font-size: 3em; margin-bottom: 20px;">GAME OVER</h1><h2 style="color: white; font-size: 1.8em; margin-bottom: 40px;">Score: <span id="fallback-score">${this.score}</span></h2><button id="fallback-menu-btn" style="background-color: #00AA00; color: white; border: none; padding: 12px 25px; font-size: 1.5em; border-radius: 5px; cursor: pointer;">Back to Menu</button>`; document.body.appendChild(overlay); document.getElementById('fallback-menu-btn').addEventListener('click', () => { overlay.remove(); if (window.sceneManager) window.sceneManager.switchToScene("MainMenuScene"); else if (this.scene) this.scene.start("MainMenuScene"); else window.location.reload(); }); } else { document.getElementById('fallback-score').innerText = this.score; overlay.style.display = 'flex'; } } catch (e) { console.error("Error showing fallback game over:", e); }
  }

  hasCustomGameOver() { return false; }

  // --- Game Mechanics & Objects ---

  optimizeForMobile() { /* ... Implementation from previous response ... */
    const isMobile = /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent); if (!isMobile) return; this.performanceMode = true; this.updateFrequencyMultiplier = 2; if(this.physics?.world) this.physics.world.setFPS(30); if (this.greenAsteroidTimer) this.greenAsteroidTimer.delay = Phaser.Math.Between(10000, 15000); if (this.specialAsteroidTimer) this.specialAsteroidTimer.delay = Phaser.Math.Between(12000, 18000); console.log("Mobile optimizations applied.");
  }
  createBrickWall() { /* ... Implementation from previous response ... */
    if (!this.bricks || !this.cameras?.main) return; this.bricks.clear(true, true); const w = this.cameras.main.width; const h = this.cameras.main.height; const brickWidth = 70; const brickHeight = 20; const numBricks = Math.floor(w / brickWidth); const totalBrickWidth = numBricks * brickWidth; const startX = (w - totalBrickWidth) / 2 + brickWidth / 2; for (let r = 0; r < 3; r++) { for (let c = 0; c < numBricks; c++) { const brickX = startX + c * brickWidth; const brickY = h - brickHeight / 2 - (r * (brickHeight + 5)); this.bricks.create(brickX, brickY, 'brick').setImmovable(true).refreshBody(); } }
  }
  spawnAsteroid() { /* ... Implementation from previous response ... */
    if (this.gameOver || !this.asteroids || !this.cameras?.main) return null; if (this.hyperspaceMode) return this.spawnRandomHyperspaceTarget(); const width = this.cameras.main.width; const spawnX = Phaser.Math.Between(100, width - 100); const scale = Phaser.Math.FloatBetween(0.8, 1.8); const velocityY = Phaser.Math.Between(150, 250) / scale; let asteroid = this.asteroids.getFirstDead(true, spawnX, -50, 'asteroid'); if (asteroid) { asteroid.setActive(true).setVisible(true).setTexture('asteroid').clearTint().setScale(scale).setData({ isGreen: false, isSpecial: false, isHyperspace: false, size: scale }); if (asteroid.body) { asteroid.body.setVelocity(0, velocityY).setAngularVelocity(Phaser.Math.Between(-50, 50)); asteroid.body.enable = true; } else console.warn("Spawned asteroid missing physics body!"); this.cleanupAsteroidTimers(asteroid); } return asteroid;
  }

  // --- MODIFIED spawnGreenAsteroid ---
  spawnGreenAsteroid(isHyperspaceSpawn = false) {
    if (this.gameOver || !this.asteroids || !this.cameras?.main) return null;
    const width = this.cameras.main.width; const height = this.cameras.main.height;
    let spawnX, spawnY, velocityX, scale, isHyperspaceFlag, tint, textureKey;

    textureKey = 'greenAsteroid'; // Always use green texture

    if (isHyperspaceSpawn) {
        // Hyperspace Mode: Spawn right, move left, larger size
        spawnX = width + Phaser.Math.Between(50, 150);
        spawnY = Phaser.Math.Between(height * 0.1, height * 0.9);
        velocityX = Phaser.Math.Between(-350, -250);
        scale = Phaser.Math.FloatBetween(2.0, 7.0); // 2x to 7x size
        isHyperspaceFlag = true;
        tint = 0xFFFFFF; // No tint needed for green texture
        console.log(`Spawning HYPERSPACE GREEN asteroid: Scale=${scale.toFixed(1)}`);
    } else {
        // Normal Mode: Spawn sides, move horizontally, normal size
        const isLeft = Phaser.Math.Between(0, 1) === 0;
        spawnY = Phaser.Math.Between(height * 0.1, height * 0.7);
        spawnX = isLeft ? -50 : width + 50;
        velocityX = isLeft ? Phaser.Math.Between(100, 200) : Phaser.Math.Between(-200, -100);
        scale = Phaser.Math.FloatBetween(1.0, 2.0);
        isHyperspaceFlag = false;
        tint = 0xFFFFFF; // No tint needed
    }

    let asteroid = this.asteroids.getFirstDead(true, spawnX, spawnY, textureKey);
    if (asteroid) {
        asteroid.setActive(true).setVisible(true)
            .setTexture(textureKey)
            .setTint(tint)
            .setScale(scale)
            .setData({ isGreen: true, isSpecial: false, isHyperspace: isHyperspaceFlag, size: scale });

        if (asteroid.body) {
            asteroid.body.setVelocity(velocityX, 0).setAngularVelocity(0);
            asteroid.body.enable = true;
        } else { console.warn("Spawned green asteroid missing physics body!"); }
        this.cleanupAsteroidTimers(asteroid);
        const destroyDelay = isHyperspaceSpawn ? 8000 : 15000;
        asteroid.setData("destroyTimer", this.time.delayedCall(destroyDelay, () => { if (asteroid?.active) asteroid.destroy(); }));
    }
    return asteroid;
  }
  // --- END MODIFIED ---

  spawnSpecialAsteroid() { /* ... Implementation from previous response ... */
    if (this.gameOver || this.hyperspaceMode || !this.asteroids || !this.cameras?.main) return null; const width = this.cameras.main.width; const height = this.cameras.main.height; const spawnY = Phaser.Math.Between(height * 0.2, height * 0.6); const isLeft = Phaser.Math.Between(0, 1) === 0; const spawnX = isLeft ? -50 : width + 50; const velocityX = isLeft ? Phaser.Math.Between(180, 280) : Phaser.Math.Between(-280, -180); const scale = 1.8; let asteroid = this.asteroids.getFirstDead(true, spawnX, spawnY, 'asteroid'); if (asteroid) { asteroid.setActive(true).setVisible(true).setTexture('asteroid').setTint(0x9932CC).setScale(scale).setData({ isGreen: false, isSpecial: true, isHyperspace: false, size: scale }); if (asteroid.body) { asteroid.body.setVelocity(velocityX, 0).setAngularVelocity(Phaser.Math.Between(-30, 30)); asteroid.body.enable = true; } else console.warn("Spawned special asteroid missing physics body!"); this.cleanupAsteroidTimers(asteroid); this.tweens?.add({ targets: asteroid, alpha: 0.7, duration: 400, yoyo: true, repeat: -1 }); asteroid.setData("destroyTimer", this.time.delayedCall(12000, () => { if (asteroid?.active) asteroid.destroy(); })); } return asteroid;
  }
  // --- METHOD: spawnHyperspaceAsteroid (MODIFIED: Uses new speed constants) ---
spawnHyperspaceAsteroid() {
  if (this.gameOver || !this.hyperspaceMode || !this.asteroids || !this.cameras?.main) return null;
  const width = this.cameras.main.width;
  const height = this.cameras.main.height;
  const spawnX = width + Phaser.Math.Between(50, 150);
  const spawnY = Phaser.Math.Between(height * 0.1, height * 0.9);
  const scale = Phaser.Math.FloatBetween(0.5, 2.5);
  // --- Uses Updated Constants ---
  const velocityX = Phaser.Math.Between(HYPERSPACE_ASTEROID_MIN_SPEED, HYPERSPACE_ASTEROID_MAX_SPEED);
  // --- End Update ---
  const bonusMultiplier = Math.max(1, Math.round(3 / scale));
  let asteroid = this.asteroids.getFirstDead(true, spawnX, spawnY, 'asteroid');
  if (asteroid) {
      asteroid.setActive(true).setVisible(true)
          .setTexture('asteroid').setTint(0x00FFFF)
          .setScale(scale)
          .setData({ isGreen: false, isSpecial: false, isHyperspace: true, size: scale, bonusMultiplier: bonusMultiplier });
      if (asteroid.body) {
          asteroid.body.setVelocity(velocityX, 0).setAngularVelocity(0);
          asteroid.body.enable = true;
      } else console.warn("Spawned hyperspace asteroid missing physics body!");
      this.cleanupAsteroidTimers(asteroid);
      asteroid.setData("destroyTimer", this.time.delayedCall(8000, () => { if (asteroid?.active) asteroid.destroy(); }));
  }
  return asteroid;
}
  spawnRandomHyperspaceTarget() { /* ... Implementation from previous response ... */
    if (Math.random() < HYPERSPACE_GREEN_CHANCE) this.spawnGreenAsteroid(true); else this.spawnHyperspaceAsteroid(); // Pass true for green
  }
  cleanupAsteroidTimers(asteroid) { /* ... Implementation from previous response ... */
    if (!asteroid?.getData) return; const timerKeys = ["destroyTimer", "trailTimer", "safetyTimer", "velocityCheckTimer"]; timerKeys.forEach(key => { const timer = asteroid.getData(key); if (timer?.remove) try { timer.remove(); } catch (e) {} asteroid.setData(key, null); });
  }
  createExplosionEffect(asteroid) { /* ... Implementation from previous response ... */
    if (this.performanceMode || !asteroid?.active || !this.add?.circle || !this.tweens) return; try { const count = 8; const color = asteroid.getData("isGreen") ? 0x00FF00 : asteroid.getData("isHyperspace") ? 0x00FFFF : asteroid.getData("isSpecial") ? 0x9932CC : 0xFFFFFF; for (let i = 0; i < count; i++) { const size = Phaser.Math.Between(2, 5); const angle = Math.PI * 2 * (i / count); const distance = Phaser.Math.Between(20, 40); const particle = this.add.circle(asteroid.x, asteroid.y, size, color, 0.9).setDepth(DEPTHS.EFFECTS); this.tweens.add({ targets: particle, x: asteroid.x + Math.cos(angle) * distance, y: asteroid.y + Math.sin(angle) * distance, alpha: 0, scale: 0.3, duration: 400, ease: "Quad.easeOut", onComplete: () => particle.destroy() }); } } catch (e) { console.warn("Error creating explosion effect:", e); }
  }
  createHyperspaceHitEffect(x, y) { /* ... Implementation from previous response ... */
    if (this.performanceMode) return; const particleCount = 12; const particleColor = 0x00FFFF; for (let i = 0; i < particleCount; i++) { const particle = this.add.circle(x, y, Phaser.Math.Between(2, 5), particleColor, 0.9).setDepth(DEPTHS.EFFECTS); const angle = Phaser.Math.FloatBetween(0, Math.PI * 2); const distance = Phaser.Math.Between(40, 80); const duration = Phaser.Math.Between(400, 700); this.tweens.add({ targets: particle, x: x + Math.cos(angle) * distance, y: y + Math.sin(angle) * distance, alpha: 0, scale: 0.1, duration: duration, ease: 'Quad.easeOut', onComplete: () => particle.destroy() }); }
  }
  createHyperspaceTrailExplosion(x, y) { /* ... Implementation from previous response ... */
    if (this.performanceMode) return; const particleCount = 15; const colors = [0xFFD700, 0xFFFFFF, 0x00FFFF]; for (let i = 0; i < particleCount; i++) { const particle = this.add.circle(x + Phaser.Math.Between(-10, 10), y + Phaser.Math.Between(-10, 10), Phaser.Math.Between(3, 7), Phaser.Utils.Array.GetRandom(colors), 1.0).setDepth(DEPTHS.EFFECTS).setBlendMode(Phaser.BlendModes.ADD); const angle = Phaser.Math.FloatBetween(0, Math.PI * 2); const distance = Phaser.Math.Between(60, 120); const duration = Phaser.Math.Between(500, 800); this.tweens.add({ targets: particle, x: x + Math.cos(angle) * distance, y: y + Math.sin(angle) * distance, alpha: 0, scale: 0.2, duration: duration, ease: 'Expo.easeOut', onComplete: () => particle.destroy() }); } const flash = this.add.circle(x, y, 40, 0xFFFFFF, 0.8).setDepth(DEPTHS.EFFECTS + 1).setBlendMode(Phaser.BlendModes.ADD); this.tweens.add({ targets: flash, scale: 0.1, alpha: 0, duration: 250, ease: 'Quad.easeOut', onComplete: () => flash.destroy() });
  }
  checkForFrozenAsteroids() {
    if (!this.asteroids?.getChildren || !this.cameras?.main) return;
    const asteroids = this.asteroids.getChildren();
    if (!Array.isArray(asteroids)) return;

    const width = this.cameras.main.width;
    const height = this.cameras.main.height;
    const buffer = 150; // Increased buffer slightly
    let destroyedCount = 0; // Count how many we destroy in this check

    asteroids.forEach(asteroid => {
        // --- ADDED: Check if asteroid or its body is already destroyed or inactive ---
        if (!asteroid || !asteroid.active || !asteroid.body || !asteroid.body.enable) {
            return; // Skip if already inactive/destroyed
        }
        // --- END ADDED ---

        // Fix missing texture (keep this)
        if (asteroid.frame?.name === '__MISSING') {
            console.warn("Fixing asteroid with missing texture.");
            asteroid.setTexture(asteroid.getData("isGreen") ? "greenAsteroid" : "asteroid");
            if (asteroid.getData("isHyperspace")) asteroid.setTint(0x00FFFF);
            else if (asteroid.getData("isSpecial")) asteroid.setTint(0x9932CC);
            else asteroid.clearTint();
        }

        // Check if off-screen (keep this)
        const isOffScreen = asteroid.x < -buffer || asteroid.x > width + buffer || asteroid.y < -buffer || asteroid.y > height + buffer;
        if (isOffScreen) {
            console.warn(`Destroying off-screen asteroid (${Math.round(asteroid.x)}, ${Math.round(asteroid.y)})`);
            this.cleanupAsteroidTimers(asteroid);
            asteroid.destroy(); // Destroy immediately
            destroyedCount++; // Increment count
            return; // Continue to next asteroid
        }

        // Check if frozen (keep this)
        const velocity = asteroid.body.velocity;
        const isFrozen = Math.abs(velocity.x) < 1 && Math.abs(velocity.y) < 1;
        if (isFrozen) {
            console.warn("Destroying frozen asteroid.");
            this.cleanupAsteroidTimers(asteroid);
            asteroid.destroy(); // Destroy immediately
            destroyedCount++; // Increment count
            return; // Continue to next asteroid
        }
    });

    // --- ADDED: Respawn logic after iterating ---
    // Count how many asteroids are currently active
    const activeCount = this.asteroids.countActive(true);
    const expectedCount = 5; // Or whatever your target number of asteroids is
    const neededSpawns = Math.max(0, expectedCount - activeCount, destroyedCount); // Respawn at least as many as were destroyed

    if (neededSpawns > 0) {
        console.log(`Respawning ${neededSpawns} asteroids after cleanup.`);
        for (let i = 0; i < neededSpawns; i++) {
            this.time.delayedCall(100 + i * 50, this.hyperspaceMode ? this.spawnRandomHyperspaceTarget : this.spawnAsteroid, [], this);
        }
    }
}

  // --- Collision Handlers ---

  setupColliders() { /* ... Implementation from previous response ... */
    this.physics?.world?.colliders?.destroy(); if (!this.physics || !this.asteroids || !this.bricks || !this.player || !this.trails) { console.error("Cannot setup colliders - physics or game objects missing."); return; } this.physics.add.collider(this.asteroids, this.bricks, this.breakBrick, null, this); this.physics.add.collider(this.asteroids, this.player, this.hitPlayer, null, this); this.physics.add.overlap(this.asteroids, this.trails, this.hitTrail, null, this); if (this.hyperspaceMode && this.hyperspaceBoxes?.length > 0) this.setupHyperspaceColliders();
  }
  hitPlayer(player, asteroid) {
    if (this.gameOver || this.gameOverProcessed || !player?.active) return;
    
    // Always clean up and destroy the asteroid first to prevent freezing
    this.cleanupAsteroidTimers(asteroid);
    this.createExplosionEffect(asteroid);
    if (asteroid?.active) {
      asteroid.destroy();
    }
    
    // Skip damage if player is invincible
    if (this.playerInvincible) return;
    
    try {
      if (this.exitingHyperspace && asteroid.getData("isHyperspace")) {
        return; // Just exit if we're exiting hyperspace and this is a hyperspace asteroid
      }
      
      this.playerInvincible = true;
      player.setAlpha(0.5);
      this.lives -= 1;
      this.livesText?.setText(`Lives: ${this.lives}`);
      this.showNotification(`HIT! Lives left: ${this.lives}`, "#FF5555", 1500);
      
      if (this.lives <= 0) {
        this.triggerGameOver("lives");
      } else {
        this.activateTemporaryShield(1500);
        if (this.invincibilityTimer) this.invincibilityTimer.remove();
        this.invincibilityTimer = this.time.delayedCall(1500, () => {
          this.playerInvincible = false;
          if (player?.active) player.setAlpha(1.0);
        }, [], this);
        
        this.time.delayedCall(300, this.spawnAsteroid, [], this);
        if (typeof this.onPlayerHit === 'function') this.onPlayerHit();
      }
    } catch (e) {
      console.error("Error in hitPlayer:", e);
    }
  }
// --- METHOD: breakBrick (MODIFIED: Uses SCORE_FACTOR) ---
breakBrick(asteroid, brick) {
  if (this.gameOver || !asteroid?.active || !brick?.active) return;
  this.createExplosionEffect(asteroid);
  this.cleanupAsteroidTimers(asteroid);
  asteroid.destroy(); // Destroy asteroid FIRST
  brick.destroy(); // Then destroy brick
  this.score += SCORE_BRICK_BREAK; // Use constant
  this.scoreText?.setText(`Score: ${this.score.toLocaleString()}`); // Format score
  this.time.delayedCall(100, this.spawnAsteroid, [], this);
  if (typeof this.onBrickBreak === 'function') this.onBrickBreak();
}
// --- END METHOD: breakBrick -

  // --- REVISED hitTrail METHOD ---
  
// --- METHOD: hitTrail (MODIFIED: Hyperspace trigger logic moved here) ---
// Enhanced hitTrail method with improved asteroid handling
hitTrail(asteroid, trail) {
  if (this.gameOver || !asteroid?.active || !trail?.active) return;

  // Always destroy the trail immediately to prevent double counting
  trail.destroy();

  // --- HANDLE SPECIAL ASTEROID COLLISION FIRST ---
  if (asteroid.getData && asteroid.getData("isSpecial") && !this.hyperspaceMode) {
    console.log("Trail hit Special Asteroid - Triggering Hyperspace!");
    const pointsEarned = SCORE_SPECIAL_ASTEROID_TRAIL; // Use constant for trigger points
    this.score += pointsEarned;
    this.scoreText?.setText(`Score: ${this.score.toLocaleString()}`);
    this.showNotification(`HYPERSPACE TRIGGERED! +${pointsEarned.toLocaleString()} pts!`, "#FF00FF", 2000); // Magenta notification
    
    // Create explosion for visual feedback before destroying asteroid
    this.createExplosionEffect(asteroid);
    
    // Force asteroid cleanup with multiple safety checks
    this.cleanupAsteroidTimers(asteroid);
    try {
      if (asteroid && asteroid.active) {
        asteroid.destroy();
      }
    } catch (e) {
      console.error("Error destroying special asteroid:", e);
      // Secondary attempt with delayed cleanup
      this.time.delayedCall(50, () => {
        try {
          if (asteroid && asteroid.active) {
            asteroid.destroy();
          }
        } catch (e2) {
          console.error("Secondary special asteroid cleanup failed:", e2);
        }
      });
    }
    
    // Activate hyperspace mode
    this.enableHyperspaceMode();
    
    // Call hook indicating special trigger
    this.onTrailHit(pointsEarned, true, false);
    return; // Exit early, don't process as normal hit
  }

  // --- SAFELY PROCESS NORMAL/GREEN/HYPERSPACE HITS ---
  
  // Always clean up timers first
  this.cleanupAsteroidTimers(asteroid);

  let pointsEarned = 0;
  let notificationMsg = "";
  let notificationColor = "#FFAA00";
  let shouldSpawnRegular = false;
  let shouldSpawnHyperspace = false;
  let wasHyperspaceTrailHit = this.hyperspaceMode;

  if (this.hyperspaceMode) { // DURING Hyperspace
    let baseScore = SCORE_HYPERSPACE_ASTEROID_TRAIL;
    if (asteroid.getData && asteroid.getData("isGreen")) {
      baseScore = SCORE_HYPERSPACE_GREEN_TRAIL;
    }
    pointsEarned = baseScore * HYPERSPACE_TRAIL_MULTIPLIER;
    notificationMsg = `HYPER TRAIL! +${pointsEarned.toLocaleString()} pts!`;
    notificationColor = "#FFD700";
    shouldSpawnHyperspace = true;
    this.createHyperspaceTrailExplosion(asteroid.x, asteroid.y);
  } else { // OUTSIDE Hyperspace
    if (asteroid.getData && asteroid.getData("isGreen")) {
      pointsEarned = SCORE_GREEN_ASTEROID_TRAIL;
      notificationMsg = `GREEN ASTEROID HIT! +${pointsEarned.toLocaleString()} pts!`;
      notificationColor = "#00FF00";
      shouldSpawnRegular = true;
      this.createExplosionEffect(asteroid);
    } else { // Regular asteroid
      pointsEarned = SCORE_ASTEROID_TRAIL;
      notificationMsg = `ASTEROID DESTROYED! +${pointsEarned.toLocaleString()} pts!`;
      notificationColor = "#FFAA00";
      shouldSpawnRegular = true;
      this.createExplosionEffect(asteroid);
    }
  }

  if (pointsEarned > 0) {
    this.score += pointsEarned;
    this.scoreText?.setText(`Score: ${this.score.toLocaleString()}`);
    this.showNotification(notificationMsg, notificationColor, 1200);
  }

  // SAFELY DESTROY ASTEROID with multiple safeguards
  try {
    if (asteroid && asteroid.active) {
      asteroid.destroy();
    }
  } catch (e) {
    console.error("Error destroying asteroid in hitTrail:", e);
    // Secondary attempt with delayed cleanup
    this.time.delayedCall(50, () => {
      try {
        if (asteroid && asteroid.active) {
          asteroid.destroy();
        }
      } catch (e2) {
        console.error("Secondary asteroid cleanup in hitTrail failed:", e2);
      }
    });
  }

  // IMPROVED RESPAWN LOGIC with randomized delays to prevent clumping
  if (shouldSpawnHyperspace) {
    // Use randomized delays for smoother gameplay
    this.time.delayedCall(
      Phaser.Math.Between(100, 400), 
      this.spawnRandomHyperspaceTarget, 
      [], 
      this
    );
  } else if (shouldSpawnRegular) {
    // Use randomized delays for smoother gameplay
    this.time.delayedCall(
      Phaser.Math.Between(100, 400), 
      this.spawnAsteroid, 
      [], 
      this
    );
  }

  // Call hook - special trigger is handled above, so it's always false here
  this.onTrailHit(pointsEarned, false, wasHyperspaceTrailHit);
}
// --- END METHOD: hitTrail -
// --- END METHOD: hitTrail ---
  // --- END REVISED hitTrail METHOD ---

// --- METHOD: hyperspaceAsteroidCollision (MODIFIED: Uses SCORE_FACTOR) ---
hyperspaceAsteroidCollision(boxOrArea, asteroid) {
  if (this.gameOver || !asteroid?.active || !asteroid.getData("isHyperspace")) return;
  const collisionPoint = { x: asteroid.x, y: asteroid.y };
  const playerDistance = Phaser.Math.Distance.Between(this.player.x, this.player.y, collisionPoint.x, collisionPoint.y);
  if (playerDistance > 100) return; // Only trigger if player is close

  this.createHyperspaceHitEffect(collisionPoint.x, collisionPoint.y);
  const pointsEarned = SCORE_HYPERSPACE_BOX_HIT; // Use constant
  this.score += pointsEarned;
  this.scoreText?.setText(`Score: ${this.score.toLocaleString()}`); // Format score

  const message = `HYPERSPACE HIT! +${pointsEarned.toLocaleString()} pts!`;
  const color = "#00FFFF";
  if (this.mpDashboard?.addEvent) this.mpDashboard.addEvent(message, color);
  else if (this.showNotification) this.showNotification(message, color, 1500);

  this.cleanupAsteroidTimers(asteroid);
  if (asteroid?.active) asteroid.destroy(); // Destroy asteroid

  if (this.hyperspaceMode) {
      this.time.delayedCall(Phaser.Math.Between(300, 800), this.spawnRandomHyperspaceTarget, [], this);
  }
}


  hyperspaceCollisionCheck(boxOrArea, asteroid) {
    if (!asteroid?.getData("isHyperspace")) return false;
    const playerDistance = Phaser.Math.Distance.Between(this.player.x, this.player.y, asteroid.x, asteroid.y);
    return playerDistance < 100;
  }

  // --- Input & Movement ---

  handleKeyDown(event) { /* ... Implementation from previous response ... */
    if (event.key === "Alt") { event.preventDefault(); event.stopPropagation(); } this.handleShortcuts(event);
  }
  handleShortcuts(event) { /* ... Implementation from previous response ... */
    if (this.gameOver) return; const now = this.time.now; if (now - this.lastKeyTime > this.sequenceTimeout) this.shortcutSequence = []; this.lastKeyTime = now; if (event.key === "Alt") { if (this.shortcutSequence[this.shortcutSequence.length - 1] !== "alt") this.shortcutSequence.push("alt"); return; } if (event.altKey && this.shortcutSequence[this.shortcutSequence.length - 1] !== "alt") this.shortcutSequence.push("alt"); const pressed = event.key.toLowerCase(); if (!["control", "shift", "alt", "meta"].includes(pressed)) this.shortcutSequence.push(pressed); const seqStr = this.shortcutSequence.join(","); const combos = { "alt,h,g,a,l": () => this.movePlayer("l"), "alt,h,g,a,r": () => this.movePlayer("r"), "alt,h,g,a,t": () => this.movePlayer("t"), "alt,h,g,a,b": () => this.movePlayer("b"), }; if (combos[seqStr]) { combos[seqStr](); this.shortcutSequence = []; } else { if (!Object.keys(combos).some(k => k.startsWith(seqStr))) this.shortcutSequence = []; }
  }

  // --- MODIFIED movePlayer (Increased Trail Duration) ---
  movePlayer(direction) {
    if (this.hyperspaceMode) { this.movePlayerHyperspace(direction); return; }
    if (this.gameOver || !this.player?.active || !this.blueBoxes || this.blueBoxes.length < 4) return;
    
    // Find closest box to player
    let currentBoxIndex = -1; 
    let minDistance = Infinity;
    this.blueBoxes.forEach((box, index) => { 
      if(box?.active) { 
        const dist = Phaser.Math.Distance.Between(this.player.x, this.player.y, box.x, box.y); 
        if (dist < minDistance) { 
          minDistance = dist; 
          currentBoxIndex = index; 
        } 
      } 
    });
    
    if (currentBoxIndex === -1) { 
      console.warn("Could not determine player's current box."); 
      return; 
    }
    
    // Determine target box based on direction
    let targetBoxIndex = -1;
    if (direction === "l") targetBoxIndex = (currentBoxIndex === 1 || currentBoxIndex === 3) ? currentBoxIndex - 1 : currentBoxIndex;
    else if (direction === "r") targetBoxIndex = (currentBoxIndex === 0 || currentBoxIndex === 2) ? currentBoxIndex + 1 : currentBoxIndex;
    else if (direction === "t") targetBoxIndex = (currentBoxIndex === 2 || currentBoxIndex === 3) ? currentBoxIndex - 2 : currentBoxIndex;
    else if (direction === "b") targetBoxIndex = (currentBoxIndex === 0 || currentBoxIndex === 1) ? currentBoxIndex + 2 : currentBoxIndex;
    
    if (targetBoxIndex < 0 || targetBoxIndex >= this.blueBoxes.length || !this.blueBoxes[targetBoxIndex]?.active) return;
    
    const startX = this.player.x; 
    const startY = this.player.y;
    const targetBox = this.blueBoxes[targetBoxIndex];
    const endX = targetBox.x; 
    const endY = targetBox.y;
    
    // Skip if position is too close
    if (Phaser.Math.Distance.Between(startX, startY, endX, endY) < 5) return;
    
    this.lastKeyTime = this.time.now;
    
    // Move player
    this.tweens?.add({ 
      targets: this.player, 
      x: endX, 
      y: endY, 
      duration: 150, 
      ease: 'Linear' 
    });
    
    // IMPROVED TRAIL CREATION: Always create a trail of appropriate size and orientation
    try {
      const horizontalMovement = Math.abs(endX - startX) > Math.abs(endY - startY);
      const trailHeight = horizontalMovement ? 20 : Math.abs(endY - startY);
      const trailWidth = horizontalMovement ? Math.abs(endX - startX) : 20;
      
      // Only create if there's an actual distance to cover
      if ((horizontalMovement && trailWidth > 10) || (!horizontalMovement && trailHeight > 10)) {
        const trailX = horizontalMovement ? (startX + endX) / 2 : startX;
        const trailY = horizontalMovement ? startY : (startY + endY) / 2;
        
        const trail = this.add.rectangle(trailX, trailY, trailWidth, trailHeight, 0xffff00);
        trail.setOrigin(0.5).setDepth(DEPTHS.TRAILS);
        this.physics.add.existing(trail, true);
        this.trails.add(trail);
        
        // Use updated constants for trail duration
        this.tweens?.add({ 
          targets: trail, 
          alpha: 0, 
          duration: TRAIL_FADE_DURATION, 
          delay: TRAIL_FADE_DELAY, 
          onComplete: () => trail.destroy() 
        });
      }
    } catch (err) { 
      console.error("Error creating trail:", err); 
    }
    
    this.onPlayerMove(endX, endY, direction);
  }
// --- END METHOD: movePlayer ---
  // --- END MODIFIED ---

  // --- MODIFIED movePlayerHyperspace (Increased Trail Duration) ---
  // --- METHOD: movePlayerHyperspace (MODIFIED: Increased Trail Duration) ---
// --- METHOD: movePlayerHyperspace (Uses updated HYPERSPACE_TRAIL constants) ---
movePlayerHyperspace(direction) {
  if (this.gameOver || !this.player?.active || !this.hyperspaceBoxes || this.hyperspaceBoxes.length < 2) return;
  
  const topBox = this.hyperspaceBoxes[0]; 
  const bottomBox = this.hyperspaceBoxes[1];
  
  if (!topBox?.active || !bottomBox?.active) return;
  
  const topY = topBox.y; 
  const bottomY = bottomBox.y; 
  const targetX = topBox.x;
  let targetY = this.player.y;
  
  if (direction === 't') targetY = topY; 
  else if (direction === 'b') targetY = bottomY; 
  else return;
  
  // Skip if already at the target position
  if (Math.abs(this.player.y - targetY) < 5) return;
  
  const startX = this.player.x; 
  const startY = this.player.y;
  this.lastKeyTime = this.time.now;
  
  // Move player
  this.tweens?.add({ 
    targets: this.player, 
    y: targetY, 
    x: targetX, 
    duration: 100, 
    ease: 'Linear',
    onUpdate: () => { 
      if (this.playerShield?.active) this.playerShield.setPosition(this.player.x, this.player.y); 
    },
    onComplete: () => { 
      if (this.playerShield?.active) this.playerShield.setPosition(this.player.x, this.player.y); 
    }
  });
  
  // IMPROVED: Always create a trail when there's enough distance
  try {
    const trailHeight = Math.abs(targetY - startY);
    if (trailHeight > 10) {
      const midY = (startY + targetY) / 2;
      const trail = this.add.rectangle(targetX, midY, 20, trailHeight, 0xffff00);
      trail.setOrigin(0.5).setDepth(DEPTHS.TRAILS);
      this.physics.add.existing(trail, true); 
      this.trails.add(trail);
      
      // Use updated constants specific to hyperspace
      this.tweens?.add({ 
        targets: trail, 
        alpha: 0, 
        duration: HYPERSPACE_TRAIL_FADE_DURATION, 
        delay: HYPERSPACE_TRAIL_FADE_DELAY, 
        onComplete: () => trail.destroy() 
      });
    }
  } catch (err) { 
    console.error("Error creating hyperspace trail:", err); 
  }
  
  this.onPlayerMove(targetX, targetY, direction);
}
// --- END METHOD: movePlayerHyperspace ---
// --- END METHOD: movePlayerHyperspace ---
  // --- END MODIFIED ---

  movePlayerToStart() { /* ... Implementation from previous response (v8) ... */
    this.time.delayedCall(1000, () => { if (this.gameOver || !this.player?.active || !this.blueBoxes || this.blueBoxes.length < 4) return; const targetBoxIndex = Math.random() > 0.5 ? 3 : 2; const targetBox = this.blueBoxes[targetBoxIndex]; if (targetBox?.active) { console.log(`(${this.scene.key}) Moving player to start box ${targetBoxIndex} at (${targetBox.x}, ${targetBox.y})`); this.tweens?.add({ targets: this.player, x: targetBox.x, y: targetBox.y, duration: 800, ease: 'Sine.easeInOut' }); } else { console.warn(`(${this.scene.key}) Target start box ${targetBoxIndex} is not active.`); const w = this.cameras.main.width; const h = this.cameras.main.height; this.player.setPosition(w / 2, h * 0.85); } }, [], this);
  }
  isPlayerInBlueBox() { /* ... Implementation from previous response ... */
    if(!this.player||!this.blueBoxes||this.blueBoxes.length<4)return false; const thresh=60; for(const box of this.blueBoxes){if(box?.active){const dist=Phaser.Math.Distance.Between(this.player.x,this.player.y,box.x,box.y); if(dist<thresh)return true;}} return false;
  }

  // --- Hyperspace Mode Logic ---
  enableHyperspaceMode() { /* ... Implementation from previous response ... */
    if(this.hyperspaceMode||this.gameOver)return; this.pausedAsteroids=[]; if(this.hyperspaceColliders){this.hyperspaceColliders.forEach(c=>c?.destroy());this.hyperspaceColliders=[];} if(this.asteroids){this.asteroids.getChildren().forEach(a=>{if(a?.active){const d={x:a.x,y:a.y,vX:a.body?.velocity.x||0,vY:a.body?.velocity.y||0,size:a.getData("size")||1.0,isGreen:a.getData("isGreen")||false,isSpecial:a.getData("isSpecial")||false}; this.pausedAsteroids.push(d); this.cleanupAsteroidTimers(a);a.destroy();}});} this.setGameState(BaseGameScene.GAME_STATES.HYPERSPACE); this.hyperspaceMode=true; const w=this.cameras.main.width; const h=this.cameras.main.height; this.blueBoxes?.forEach(b=>b.setVisible(false)); this.bricks?.getChildren().forEach(b=>b.setVisible(false)); this.originalPlayerPos={x:this.player.x,y:this.player.y}; this.hyperspaceBackground?.destroy(); this.starParticles?.forEach(p=>p?.destroy());this.starParticles=[]; const tBg=this.add.rectangle(0,0,w,h,0x000000,0).setOrigin(0,0).setDepth(100); this.tweens.add({targets:tBg,alpha:0.95,duration:1000,ease:'Sine.easeInOut',onComplete:()=>{this.startHyperspaceSequence(tBg);}});
  }
  startHyperspaceSequence(transitionBg) { /* ... Implementation from previous response (uses spawnRandomHyperspaceTarget) ... */
    if(this.gameOver){transitionBg?.destroy();return;} const w=this.cameras.main.width; const h=this.cameras.main.height; this.hyperspaceBackground=this.add.rectangle(0,0,w,h,0x000022).setOrigin(0,0).setDepth(-10); this.hyperspaceBoxes=[]; const tB=this.add.rectangle(w/2,h*0.25,100,100,0x0099FF).setStrokeStyle(3,0x00FFFF).setData('position','top').setAlpha(0).setDepth(10); this.hyperspaceBoxes.push(tB); const bB=this.add.rectangle(w/2,h*0.75,100,100,0x0099FF).setStrokeStyle(3,0x00FFFF).setData('position','bottom').setAlpha(0).setDepth(10); this.hyperspaceBoxes.push(bB); this.setupHyperspaceColliders(); this.playerInvincible=true; this.playerShield=this.add.circle(this.player.x,this.player.y,50,0x00FFFF,0.4).setDepth(1000); this.tweens.add({targets:this.playerShield,alpha:{from:0.4,to:0.7},scale:{from:0.9,to:1.1},duration:800,yoyo:true,repeat:-1}); this.player.setDepth(999); const cX=w/2; const cY=h/2; this.physics.pause(); const cL=this.add.circle(cX,cY,5,0xFFFFFF,1).setDepth(200); this.tweens.add({targets:cL,radius:40,duration:1200,ease:'Sine.easeIn',onComplete:()=>{ cL.destroy(); this.createHyperspaceStars(); this.tweens.add({targets:transitionBg,alpha:0,duration:1000,delay:500,onComplete:()=>transitionBg?.destroy()}); this.tweens.add({targets:this.hyperspaceBoxes,alpha:1,duration:1000,delay:1000}); this.tweens.add({targets:this.player,x:w/2,y:h*0.75,duration:1000,ease:'Sine.easeInOut',onUpdate:()=>{if(this.playerShield)this.playerShield.setPosition(this.player.x,this.player.y);},onComplete:()=>this.physics.resume()}); this.time.delayedCall(2000,this.createContinuousStarField,[],this); this.time.delayedCall(2200,this.showHyperspaceBanner,[],this); this.time.delayedCall(4000,()=>{this.hyperspaceAsteroidTimer=this.time.addEvent({delay:HYPERSPACE_ASTEROID_INTERVAL,callback:this.spawnRandomHyperspaceTarget,callbackScope:this,loop:true});},[],this); }}); if(this.hyperspaceTimer) this.hyperspaceTimer.remove(); this.hyperspaceTimer=this.time.delayedCall(this.hyperspaceDuration,this.disableHyperspaceMode,[],this); this.onHyperspaceActivated();
  }
  createHyperspaceStars() { /* ... Implementation from previous response ... */
    if(this.gameOver)return; const w=this.cameras.main.width; const h=this.cameras.main.height; const cX=w/2; const cY=h/2; const count=this.performanceMode?100:250; const stars=[]; for(let i=0;i<count;i++){ const s=this.add.circle(cX+Phaser.Math.Between(-30,30),cY+Phaser.Math.Between(-30,30),Phaser.Math.FloatBetween(1,4),0xFFFFFF).setDepth(100); stars.push(s); const angle=Phaser.Math.FloatBetween(0,Math.PI*2); const dist=Math.max(w,h)*2; const tX=cX+Math.cos(angle)*dist; const tY=cY+Math.sin(angle)*dist; this.tweens.add({targets:s,x:tX,y:tY,scaleX:35,scaleY:0.2,duration:Phaser.Math.Between(1500,2500),ease:'Quad.easeOut',onComplete:()=>s.destroy()}); }
  }
  createContinuousStarField() { /* ... Implementation from previous response ... */
    if(!this.hyperspaceMode||this.gameOver)return; const w=this.cameras.main.width; const h=this.cameras.main.height; this.starParticles=[]; const delay=this.performanceMode?60:30; const batch=this.performanceMode?3:5; const createStream=(edge,dX,dY)=>{const timer=this.time.addEvent({delay:delay,repeat:-1,callback:()=>{if(!this.hyperspaceMode||this.gameOver){timer.remove();return;} for(let i=0;i<Phaser.Math.Between(1,batch);i++){let x,y; if(edge==='l'){x=0;y=Phaser.Math.Between(0,h);}else if(edge==='r'){x=w;y=Phaser.Math.Between(0,h);}else if(edge==='t'){x=Phaser.Math.Between(0,w);y=0;}else{x=Phaser.Math.Between(0,w);y=h;} const clrs=[0xFFFFFF,0xCCFFFF,0x99CCFF,0x6699FF,0x00CCFF]; const s=this.add.circle(x,y,Phaser.Math.FloatBetween(1.5,4),clrs[Math.floor(Math.random()*clrs.length)]).setDepth(0); this.starParticles.push(s); this.tweens.add({targets:s,x:x+(dX*w*1.5),y:y+(dY*h*1.5),alpha:0,duration:Phaser.Math.Between(800,1500),onComplete:()=>{const idx=this.starParticles.indexOf(s); if(idx>-1)this.starParticles.splice(idx,1); s.destroy();}});}}}); return timer;}; if(this.starTimers) this.starTimers.forEach(t=>t?.remove()); this.starTimers=[]; this.starTimers.push(createStream('l',1,0)); this.starTimers.push(createStream('r',-1,0)); this.starTimers.push(createStream('t',0,1)); this.starTimers.push(createStream('b',0,-1));
  }
  showHyperspaceBanner() { /* ... Implementation from previous response ... */
    if(this.gameOver)return; const w=this.cameras.main.width; const h=this.cameras.main.height; const bg=this.add.rectangle(w/2,h/2,w*0.9,h*0.3,0x000000,0.8).setOrigin(0.5).setDepth(500); const glow=this.add.rectangle(w/2,h/2,w*0.92,h*0.32,0x0066FF,0.3).setOrigin(0.5).setDepth(499); const txt=this.add.text(w/2,h/2,'HYPERSPACE ACTIVATED',{fontFamily:'Arial',fontSize:'48px',fontWeight:'bold',color:'#00FFFF',stroke:'#000088',strokeThickness:8,align:'center',shadow:{offsetX:0,offsetY:0,color:'#00CCFF',blur:15,stroke:true,fill:true}}).setOrigin(0.5).setDepth(501); const sub=this.add.text(w/2,h/2+50,'PREPARE FOR LIGHTSPEED',{fontFamily:'Arial',fontSize:'24px',color:'#FFFFFF',align:'center'}).setOrigin(0.5).setDepth(501); this.tweens.add({targets:txt,scale:1.1,duration:500,yoyo:true,repeat:4}); const elems=[bg,glow,txt,sub]; this.tweens.add({targets:elems,alpha:0,delay:4000,duration:1000,onComplete:()=>elems.forEach(el=>el?.destroy())});
  }
  disableHyperspaceMode() { /* ... Implementation from previous response ... */
    if(!this.hyperspaceMode||this.gameOver)return; this.exitingHyperspace=true; this.time.delayedCall(2000,()=>{this.exitingHyperspace=false;},[],this); if(this.hyperspaceColliders){this.hyperspaceColliders.forEach(c=>c?.destroy());this.hyperspaceColliders=[];} if(this.asteroids){this.asteroids.getChildren().forEach(a=>{if(a?.active){this.cleanupAsteroidTimers(a);a.destroy();}});} this.hyperspaceAsteroidTimer?.remove(); this.hyperspaceAsteroidTimer=null; this.setGameState(BaseGameScene.GAME_STATES.NORMAL); this.hyperspaceMode=false; this.playerInvincible=true; const w=this.cameras.main.width; const h=this.cameras.main.height; const flash=this.add.rectangle(0,0,w,h,0xFFFFFF,0.7).setOrigin(0,0).setDepth(9999); this.restoreNormalGameElements(); const{targetX,targetY}=this.getPlayerReturnPosition(); this.createReverseHyperspaceStars(); this.showTransitionMessage(flash); this.movePlayerToNormalPosition(targetX,targetY);
  }
  restoreNormalGameElements() { /* ... Implementation from previous response ... */
    if(this.blueBoxes){this.blueBoxes.forEach(b=>{b.setVisible(true).setAlpha(0);}); this.tweens.add({targets:this.blueBoxes,alpha:1,duration:1000,ease:'Sine.easeIn'});} if(this.bricks){const ch=this.bricks.getChildren(); const B=20; for(let i=0;i<ch.length;i+=B){const ba=ch.slice(i,i+B); ba.forEach(br=>{br.setVisible(true).setAlpha(0);}); this.tweens.add({targets:ba,alpha:1,duration:1000,ease:'Sine.easeIn'});}}
  }
  getPlayerReturnPosition() { /* ... Implementation from previous response ... */
    const w=this.cameras.main.width; const h=this.cameras.main.height; const right=Math.random()>0.5; let tX,tY; if(this.blueBoxes?.length>=4){const idx=right?3:2; tX=this.blueBoxes[idx].x; tY=this.blueBoxes[idx].y;}else{tX=right?w*0.9:w*0.1; tY=h*0.85;} return{targetX:tX,targetY:tY};
  }
  showTransitionMessage(flash) { /* ... Implementation from previous response ... */
    const w=this.cameras.main.width; const h=this.cameras.main.height; this.tweens.add({targets:flash,alpha:0,duration:800,onComplete:()=>{flash.destroy(); const txt=this.add.text(w/2,h/2,'RETURNING TO NORMAL SPACE',{fontFamily:'Arial',fontSize:'36px',color:'#FFFFFF',stroke:'#000',strokeThickness:6,align:'center'}).setOrigin(0.5).setDepth(10001); this.tweens.add({targets:txt,alpha:0,delay:1500,duration:1000,onComplete:()=>txt.destroy()});}});
  }
  movePlayerToNormalPosition(targetX, targetY) { /* ... Implementation from previous response ... */
    this.tweens.add({targets:this.player,x:targetX,y:targetY,duration:1500,ease:'Sine.easeInOut',onUpdate:()=>{if(this.playerShield)this.playerShield.setPosition(this.player.x,this.player.y);},onComplete:()=>{this.activateTemporaryShield(3000); this.cleanupHyperspaceElements(); this.time.delayedCall(3000,()=>{this.playerInvincible=false;}); this.spawnAsteroid(); this.resetCollisions(); this.exitingHyperspace=false; this.onHyperspaceDeactivated();}});
  }
  cleanupHyperspaceElements() { /* ... Implementation from previous response ... */
    const fadeOut=[]; if(this.hyperspaceBackground)fadeOut.push(this.hyperspaceBackground); if(this.playerShield){this.tweens.add({targets:this.playerShield,alpha:0,scale:1.5,duration:800,onComplete:()=>{this.playerShield?.destroy();this.playerShield=null;}});} if(this.hyperspaceBoxes){this.tweens.add({targets:this.hyperspaceBoxes,alpha:0,duration:800,onComplete:()=>{this.hyperspaceBoxes.forEach(b=>b.destroy());this.hyperspaceBoxes=[];}});} if(fadeOut.length>0){this.tweens.add({targets:fadeOut,alpha:0,duration:1000,onComplete:()=>fadeOut.forEach(el=>el?.destroy())});} if(this.starParticles){const B=25;for(let i=0;i<this.starParticles.length;i+=B){const ba=this.starParticles.slice(i,i+B); this.tweens.add({targets:ba.filter(p=>p?.active),alpha:0,duration:500,onComplete:()=>ba.forEach(p=>p?.destroy())});} this.starParticles=[];} if(this.starTimers){this.starTimers.forEach(t=>t?.remove()); this.starTimers=[];} if(this.hyperspaceColliders){this.hyperspaceColliders.forEach(c=>c?.destroy()); this.hyperspaceColliders=[];}
  }
  resetCollisions() { /* ... Implementation from previous response ... */
    this.time.delayedCall(500,()=>{if(this.gameOver||!this.physics.world)return; this.setupColliders();},[],this); // Call setupColliders to re-add base colliders
  }
  createReverseHyperspaceStars() { /* ... Implementation from previous response ... */
    if(this.gameOver)return; const w=this.cameras.main.width; const h=this.cameras.main.height; const cX=w/2; const cY=h/2; const count=this.performanceMode?70:150; for(let i=0;i<count;i++){ const angle=Phaser.Math.FloatBetween(0,Math.PI*2); const dist=Math.max(w,h)*Phaser.Math.FloatBetween(0.7,1.5); const sX=cX+Math.cos(angle)*dist; const sY=cY+Math.sin(angle)*dist; const star=this.add.rectangle(sX,sY,Phaser.Math.Between(30,60),Phaser.Math.Between(2,4),0xFFFFFF); star.rotation=angle+Math.PI; star.setDepth(100); this.tweens.add({targets:star,x:cX,y:cY,scaleX:0.1,scaleY:0.1,alpha:{start:0.8,to:0},duration:Phaser.Math.Between(1000,2000),ease:'Cubic.easeIn',onComplete:()=>star.destroy()}); } const cL=this.add.circle(cX,cY,40,0xFFFFFF,1).setDepth(200); this.tweens.add({targets:cL,radius:5,alpha:0,duration:1500,ease:'Sine.easeOut',onComplete:()=>cL.destroy()});
  }
  setupHyperspaceColliders() { /* ... Implementation from previous response ... */
    if(this.hyperspaceColliders){this.hyperspaceColliders.forEach(c=>c?.destroy());} this.hyperspaceColliders=[]; this.hyperspaceBoxes?.forEach(box=>{if(!box?.active) return; const area=this.add.rectangle(box.x,box.y,100,100).setAlpha(0); this.physics.add.existing(area,true); if(area.body){ area.body.setCircle(50); area.setData('position',box.getData('position')); const collider=this.physics.add.overlap(area,this.asteroids,this.hyperspaceAsteroidCollision,this.hyperspaceCollisionCheck,this); this.hyperspaceColliders.push(collider);} else {area.destroy();} });
  }
  isPlayerInHyperspaceBox(boxPosition) { /* ... Implementation from previous response ... */
    if(!this.hyperspaceMode||!this.player||!this.hyperspaceBoxes)return false; const box=this.hyperspaceBoxes.find(b=>b.getData('position')===boxPosition); if(!box)return false; const dist=Phaser.Math.Distance.Between(this.player.x,this.player.y,box.x,box.y); return dist < 80;
  }

  // --- Performance & Utils ---

  monitorPerformance(time) { /* ... Implementation from previous response ... */
    this.frameCounter++; if (time - this.lastFpsUpdateTime > this.fpsUpdateInterval) { this.currentFps = Math.round((this.frameCounter * 1000) / (time - this.lastFpsUpdateTime)); if (this.currentFps < this.lowFpsThreshold) { this.lowFpsCounter++; if (this.lowFpsCounter >= 3 && !this.performanceMode) this.enablePerformanceMode(); } else { this.lowFpsCounter = 0; } this.lastFpsUpdateTime = time; this.frameCounter = 0; }
  }
  enablePerformanceMode() { /* ... Implementation from previous response ... */
    if (this.performanceMode) return; console.log(`(${this.scene.key}) Enabling performance mode`); this.performanceMode = true; this.updateFrequencyMultiplier = 2; if (this.physics?.world) this.physics.world.setFPS(30);
  }
  disablePerformanceMode() { /* ... Implementation from previous response ... */
    if (!this.performanceMode) return; console.log(`(${this.scene.key}) Disabling performance mode`); this.performanceMode = false; this.updateFrequencyMultiplier = 1; if (this.physics?.world) this.physics.world.setFPS(60);
  }
  activateTemporaryShield(duration = 2000) { /* ... Implementation from previous response ... */
    if (this.gameOver || !this.player?.active || !this.add?.circle || !this.tweens) return; if (this.tempPlayerShield) { const existingTimer = this.tempPlayerShield.getData("timer"); if (existingTimer) existingTimer.remove(); this.tempPlayerShield.destroy(); this.tempPlayerShield = null; } this.tempPlayerShield = this.add.circle(this.player.x, this.player.y, 40, 0x00FFFF, 0.3).setDepth(DEPTHS.PLAYER - 1); this.tweens.add({ targets: this.tempPlayerShield, alpha: 0.6, scale: 1.1, duration: 400, yoyo: true, repeat: Math.floor(duration / 800) }); const timer = this.time.delayedCall(duration, () => { if (this.tempPlayerShield?.active) { this.tweens.add({ targets: this.tempPlayerShield, alpha: 0, duration: 300, onComplete: () => this.tempPlayerShield?.destroy() }); } this.tempPlayerShield = null; }); this.tempPlayerShield.setData("timer", timer);
  }
  handleTabVisibilityChange() { /* ... Implementation from previous response ... */
    if (!this.scene?.key) return; console.log(`(${this.scene.key}) Tab visibility: ${document.visibilityState}`); if (document.visibilityState === 'visible' && this.scene?.systems?.scenePlugin?.isActive(this.scene.key)) { this.time?.delayedCall(150, () => { if (!this.scene?.systems?.scenePlugin?.isActive(this.scene.key) || this.gameOver) return; if (this.physics?.world?.isPaused) this.physics.resume(); if (this.player) { this.player.alpha = 1; this.player.visible = true; } this.checkForFrozenAsteroids(); this.cameras?.main?.shake(100, 0.001); CanvasFixer.fixCanvas(); }); }
  }

  // --- Placeholders / Hooks for Subclasses ---
  customUpdate(time, delta) { /* For subclasses */ }
  onPlayerMove(newX, newY, direction) { /* For subclasses */ }
  onPlayerHit() { /* For subclasses */ }
  onBrickBreak() { /* For subclasses */ }
  onTrailHit(pointsEarned, isSpecialTrigger, isHyperspaceTrailHit = false) { /* For subclasses */ }
  onHyperspaceActivated() { /* For subclasses */ }
  onHyperspaceDeactivated() { /* For subclasses */ }

  // Export the score constants so they can be imported by other scene files

} // End of BaseGameScene class
export {
  SCORE_ASTEROID_TRAIL,
  SCORE_GREEN_ASTEROID_TRAIL,
  SCORE_SPECIAL_ASTEROID_TRAIL,
  SCORE_HYPERSPACE_ASTEROID_TRAIL,
  SCORE_HYPERSPACE_GREEN_TRAIL,
  SCORE_HYPERSPACE_BOX_HIT,
  SCORE_BRICK_BREAK
};
